/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Box, Button, TextField, Tooltip, Stack } from "@mui/material";
import { ReactComponent as Settings } from "assets/images/icons/common/settings-with-border.svg";
import { correctPosition } from "cms/common/utils/correct-position";
import { useContentZoneContext } from "cms/components/context";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";
import { SimplicityComposerEnsureTipTap } from "cms/rich-text/simplicity-composer-tiptap";
import { SVGIcons } from "cms/icons/svg-icons";
import LabelledField from "components/labelled-field";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generateGuid, isStringEmpty } from "utils/empty-exists";
import { ComponentCTAEditWrapper } from "./component-cta-wrapper";
import { ImageCardTypeButtons } from "./image-card-style";
import { CTALink } from "./link";
import { SettingsDialog } from "./settings-dialog";
import { Logger } from "cms/utils/log/logger";
import { ImageInPageEditorV2 } from "cms/images/image-inpage-editor-v2/index";

const CallToActionComponentEditor = () => {
    const { defaultImpl } = useContentZoneContext();

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const { t } = useTranslation();

    const { component, setComponent, update, updateFactory } = useUpdateComponent();
    const {
        title,
        description,
        image: _image,
        imageCard,
        links,
        settings: _settings,
    } = component || {};
    
    const [image,setImage] = useState(_image);
    const [settings,setSettings] = useState(_settings);
    const { image_settings } = _settings || {};
    
    const onChangeTitle = (event) => {
        const title = event.target.value;
        update("title", title);
    };
    const onChangeDescription = updateFactory("description");
    const onChangeImageCard = updateFactory("imageCard");
    const onChangeSettings = updateFactory("settings");
    const onChangeLinks = updateFactory("links");
    // const onChangeImageSettings = updateFactory('settings.image_settings')
    // const onChangeImage = updateFactory('image')
    
    const onChangeImageWithSettings = (image, settings) => {
        const { guid } = image || {};
        if (isStringEmpty(guid)) {
            Logger.warn("[onChangeImageWithSettings] image guid missing", { image, guid });
        } else {
            setImage(guid);
            setSettings(settings);
            update("settings.image_settings", settings);
            update("image", guid);
        }
    };
    const onRemoveImage = () => {
        setImage("");
        setSettings({});
        update("settings.image_settings", {});
        update("image", "");
    };
    const addLink = () => {
        const updatedLinks = structuredClone(links || []);
        const newLink = {
            link: "",
            guid: generateGuid(),
        };
        updatedLinks.push(newLink);

        correctPosition(updatedLinks);
        onChangeLinks(updatedLinks);
    };
    const onOpenSettings = (event) => {
        setAnchorEl(event.currentTarget);
    };

    return (
        <div className='simplicity-editor-frame'>
            {/*  Image  */}
            <LabelledField label={`${t("commons.image")} :`}>
                <div className="simplicity-image-list-item">
                    <Stack direction="row" justifyContent="flex-start">
                        <div className="simplicity-image-list-item-data" style={{ width: "100%" }}>
                            <Stack direction="column" justifyContent="flex-start" style={{ width: "100%" }}>
                                <div>
                                    <ImageInPageEditorV2
                                        image_guid={image}
                                        settings={image_settings}
                                        onChangeImageWithSettings={onChangeImageWithSettings}
                                        onRemoveImage={onRemoveImage}
                                    />
                                </div>
                            </Stack>
                        </div>
                    </Stack>
                </div>
                {/*<CTAImage*/}
                {/*    guid={image}*/}
                {/*    settings={settings}*/}
                {/*    onChangeImage={onChangeImage}*/}
                {/*    // onChangeSettings={onChangeSettings}*/}
                {/*    onChangeImageSettings={onChangeImageSettings}*/}
                {/*/>*/}
            </LabelledField>
            
            {/*  Title */}
            <LabelledField label={`${t("commons.displayTitle")} :`}>
                <TextField
                    id="section_list_title"
                    fullWidth={true}
                    value={title}
                    placeholder={t("commons.addAHeading")}
                    onChange={onChangeTitle}
                />
            </LabelledField>
            
            {/*  Description  */}
            <LabelledField label={t("commons.description")}>
                <SimplicityComposerEnsureTipTap
                    richtext={description}
                    onChange={onChangeDescription}
                />
            </LabelledField>
            
            {/*  CTA Style  */}
            <LabelledField label={t("commons.imageCardStyle")} style={{ paddingTop: 6, marginTop: 12 }}>
                <ImageCardTypeButtons
                    imageCard={imageCard}
                    onChange={onChangeImageCard}
                />
            </LabelledField>
            
            {/*  Link Settings  */}
            <LabelledField label={t("commons.links")} style={{ marginTop: 12 }}>
                <Box style={{ paddingTop: 6, display: "flex", gap: 12 }}>
                    <Tooltip
                        title={t("commons.linkSettings")}
                        placement='top'
                        arrow
                        classes={{ arrow: "arrow", tooltip: "tooltip" }}
                    >
                        <Settings onClick={onOpenSettings} />
                    </Tooltip>
                    {/*  Link Settings - Gear */}
                    <SettingsDialog
                        settings={settings}
                        onChange={onChangeSettings}
                        open={open}
                        anchorEl={anchorEl}
                        setAnchorEl={setAnchorEl}
                    />

                    {/*  Add Link button  */}
                    <Button
                        size='medium'
                        variant='contained'
                        color={"primary"}
                        target='warning'
                        onClick={addLink}
                    >
                        <SVGIcons.common.Create style={{ paddingRight: 4, cursor: "pointer" }} />{" "}
                        {t("commons.addListItem")}
                    </Button>
                </Box>
            </LabelledField>
            <ComponentCTAEditWrapper
                links={links}
                onChange={onChangeLinks}
                localState={component}
                setLocalState={setComponent}
                defaultImpl={defaultImpl}
            >
                <CTALink />
            </ComponentCTAEditWrapper>
        </div>
    );
};
CallToActionComponentEditor.displayName = "CallToActionComponentEditor";
export { CallToActionComponentEditor };


/**
 * Represents a call-to-action component.
 * @typedef {object} CallToActionComponent
 * @property {string} guid - The unique identifier for the component.
 * @property {string} type - The type of the component (e.g., "component.call-to-action").
 * @property {string} organization - The organization associated with the component.
 * @property {number} position - The position of the component.
 * @property {string} label - The label for the call-to-action.
 * @property {number} reuse_count - The number of times the component has been reused.
 * @property {boolean} isReused - Indicates whether the component is reused.
 * @property {string} title - The title of the call-to-action.
 * @property {import('@tiptap/core').JSONContent} description - The description of the call-to-action.
 * @property {string} image - The image associated with the call-to-action (empty string if none).
 * @property {string} imageCard - The card layout for the image (e.g., "horizontal").
 * @property {Array} links - An array of links associated with the call-to-action.
 * @property {Object} settings - Additional settings for the call-to-action.
 * @property {string} settings.style - The style of the call-to-action (e.g., "body").
 * @property {string} settings.format - The format of the call-to-action (e.g., "text-link").
 * @property {Object} settings.image_settings - Settings related to the image (if applicable).
 */
